import {
  AfterViewInit,
  Directive,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  Renderer2,
  SimpleChanges,
} from '@angular/core';

@Directive({
  selector: '[routeItineraryDottedLineIcon]',
})
export class RouteItineraryDottedLineIconDirective implements AfterViewInit, OnDestroy, OnChanges {
  @Input() iconClass: string = 'fa fa-circle';
  @Input() svgIconPath: string | null = null;
  @Input() iconWidth: string = '16px';
  @Input() iconHeight: string = '16px';

  private icon: HTMLElement | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private dotsContainer: HTMLElement | null = null;
  private dottedLine: Element | null = null;
  private isInitialised = false;
  private resizeDebounceTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(private el: ElementRef, private renderer: Renderer2) {}

  private debounceUpdateIconPosition() {
    if (this.resizeDebounceTimeout) {
      clearTimeout(this.resizeDebounceTimeout);
    }

    this.resizeDebounceTimeout = setTimeout(() => {
      this.updateIconPosition();
    }, 100);
  }

  ngAfterViewInit() {
    this.dotsContainer = this.findParentDotsContainer(this.el.nativeElement);
    if (!this.dotsContainer) {
      return;
    }

    this.dottedLine = this.dotsContainer.querySelector('.dotted-line');
    if (!this.dottedLine) {
      return;
    }

    this.renderer.setStyle(this.dotsContainer, 'position', 'relative');

    this.createOrUpdateIcon();

    this.resizeObserver = new ResizeObserver(() => {
      this.debounceUpdateIconPosition();
    });

    this.resizeObserver.observe(this.dotsContainer);
    this.resizeObserver.observe(this.el.nativeElement);

    this.isInitialised = true;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.isInitialised && (changes['svgIconPath'] || changes['iconClass'])) {
      const pathChanged =
        changes['svgIconPath'] &&
        changes['svgIconPath'].currentValue !== changes['svgIconPath'].previousValue;
      const classChanged =
        changes['iconClass'] &&
        changes['iconClass'].currentValue !== changes['iconClass'].previousValue;

      if (pathChanged || classChanged) {
        this.createOrUpdateIcon();
      }
    }
  }

  ngOnDestroy() {
    this.removeIcon();

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    if (this.resizeDebounceTimeout) {
      clearTimeout(this.resizeDebounceTimeout);
    }

    this.isInitialised = false;
  }

  private findParentDotsContainer(element: HTMLElement): HTMLElement | null {
    let current: HTMLElement | null = element;
    while (current && !current.classList.contains('dots')) {
      current = current.parentElement;
    }
    return current;
  }

  private removeIcon() {
    if (this.icon && this.icon.parentNode) {
      this.renderer.removeChild(this.icon.parentNode, this.icon);
      this.icon = null;
    }
  }

  private createOrUpdateIcon() {
    if (!this.dotsContainer || !this.dottedLine) {
      this.dotsContainer = this.findParentDotsContainer(this.el.nativeElement);
      this.dottedLine = this.dotsContainer?.querySelector('.dotted-line');

      if (!this.dotsContainer || !this.dottedLine) {
        return;
      }
      this.renderer.setStyle(this.dotsContainer, 'position', 'relative');
    }

    this.removeIcon();

    if (this.svgIconPath) {
      this.icon = this.renderer.createElement('img');
      this.renderer.setAttribute(this.icon, 'src', this.svgIconPath);
      this.renderer.setStyle(this.icon, 'width', this.iconWidth);
      this.renderer.setStyle(this.icon, 'height', this.iconHeight);
      this.renderer.setAttribute(this.icon, 'alt', 'Icon');
      this.renderer.addClass(this.icon, 'svg-icon');
    } else if (this.iconClass) {
      this.icon = this.renderer.createElement('i');
      this.iconClass.split(' ').forEach((cls) => {
        if (cls) this.renderer.addClass(this.icon!, cls);
      });
    } else {
      return;
    }

    this.renderer.addClass(this.icon, 'dotted-line-icon');
    this.renderer.appendChild(this.dotsContainer, this.icon);

    this.updateIconPosition();
  }

  private updateIconPosition() {
    if (!this.icon || !this.dotsContainer || !this.dottedLine) {
      return;
    }

    const containerRect = this.dotsContainer.getBoundingClientRect();
    const elementRect = this.el.nativeElement.getBoundingClientRect();

    const dottedLineRect = (this.dottedLine as HTMLElement).getBoundingClientRect();

    if (containerRect.width === 0 || elementRect.width === 0 || dottedLineRect.width === 0) {
      return;
    }

    const dottedLineLeft = dottedLineRect.left - containerRect.left;
    const dottedLineCentreX = dottedLineLeft + dottedLineRect.width / 2;

    const elementTop = elementRect.top - containerRect.top;
    const relativeTop = elementTop + elementRect.height / 2;

    this.renderer.setStyle(this.icon, 'position', 'absolute');
    this.renderer.setStyle(this.icon, 'left', `${dottedLineCentreX}px`);
    this.renderer.setStyle(this.icon, 'top', `${relativeTop}px`);
    this.renderer.setStyle(this.icon, 'transform', 'translate(-50%, -50%)');
    this.renderer.setStyle(this.icon, 'backgroundColor', 'white');
    this.renderer.setStyle(this.icon, 'padding', '0 3px');
    this.renderer.setStyle(this.icon, 'zIndex', '10');
    this.renderer.setStyle(this.icon, 'pointerEvents', 'none');
  }
}
